import {
  NOTIFICATIONS_REFETCH_INTERVAL_MS,
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type Notification,
  type NotificationFilter,
} from '../api.js';
import type { NotificationTone } from '@viquard/doc-ui';
import { useToast } from '@viquard/doc-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatNotificationTime,
  notificationCopy,
  notificationTone,
  openNotificationAction,
} from '../format.js';

export const NOTIFICATION_FILTERS: readonly NotificationFilter[] = ['all', 'unread', 'read'];

export interface NotificationInboxItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  time: string;
  unread: boolean;
  tone: NotificationTone;
  aggregateCount: number;
  aggregateLabel: string;
  source: Notification;
}

export type NotificationInboxMode = 'append' | 'paged';

export interface UseNotificationsInboxOptions {
  mode?: NotificationInboxMode;
  pageSize?: number;
  refreshIntervalMs?: number | false;
  onBeforeNavigate?: (() => void) | undefined;
}

export interface NotificationsInboxController {
  filter: NotificationFilter;
  setFilter: (filter: NotificationFilter) => void;
  items: NotificationInboxItem[];
  total: number;
  unreadCount: number;
  shown: number;
  page: number;
  pageCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isBusy: boolean;
  error: Error | null;
  refresh: () => void;
  loadMore: () => void;
  setPage: (page: number) => void;
  markRead: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  open: (item: Notification | NotificationInboxItem) => Promise<void>;
}

export function useNotificationsInbox(
  opts: UseNotificationsInboxOptions = {},
): NotificationsInboxController {
  const { t, i18n } = useTranslation('common');
  const toast = useToast();
  const mode = opts.mode ?? 'append';
  const pageSize = Math.min(100, Math.max(1, opts.pageSize ?? 20));
  const refreshIntervalMs = opts.refreshIntervalMs ?? NOTIFICATIONS_REFETCH_INTERVAL_MS;
  const [filter, setFilterState] = useState<NotificationFilter>('all');
  const [page, setPageState] = useState(1);

  const limit = mode === 'append' ? Math.min(100, page * pageSize) : pageSize;
  const offset = mode === 'paged' ? (page - 1) * pageSize : 0;
  const list = useNotifications({
    filter,
    limit,
    offset,
    refetchInterval: refreshIntervalMs,
  });
  const markReadMutation = useMarkNotificationRead();
  const markAllMutation = useMarkAllNotificationsRead();
  const dismissMutation = useDismissNotification();

  const rows = uniqueNotifications(list.data?.notifications ?? []);
  const total = list.data?.total ?? 0;
  const unreadCount = list.data?.unreadCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const shown =
    mode === 'append' ? rows.length : total === 0 ? 0 : Math.min(total, offset + rows.length);
  const hasMore = mode === 'append' && rows.length < total && limit < 100;
  const isBusy =
    markReadMutation.isPending || markAllMutation.isPending || dismissMutation.isPending;

  const items = rows.map((row) => {
    const aggregateCount = row.aggregateCount;
    return {
      id: row.id,
      kind: row.kind,
      title: notificationCopy(row.title, i18n),
      body: notificationCopy(row.body, i18n),
      time: formatNotificationTime(row.aggregateLastAt ?? row.createdAt, i18n.language),
      unread: row.readAt === null,
      tone: notificationTone(row.kind),
      aggregateCount,
      aggregateLabel: t('notifications.groupedCount', { count: aggregateCount }),
      source: row,
    };
  });

  const setFilter = (next: NotificationFilter): void => {
    setFilterState(next);
    setPageState(1);
  };

  const setPage = (next: number): void => {
    setPageState(Math.max(1, Math.floor(next)));
  };

  const refresh = (): void => {
    void list.refetch();
  };

  const loadMore = (): void => {
    if (!hasMore) return;
    setPageState((current) => current + 1);
  };

  const markRead = async (id: string): Promise<void> => {
    try {
      await markReadMutation.mutateAsync({ id });
    } catch {
      toast.danger({ title: t('notifications.actionFailed'), duration: 5000 });
    }
  };

  const dismiss = async (id: string): Promise<void> => {
    try {
      await dismissMutation.mutateAsync({ id });
    } catch {
      toast.danger({ title: t('notifications.actionFailed'), duration: 5000 });
    }
  };

  const markAllRead = async (): Promise<void> => {
    try {
      const result = await markAllMutation.mutateAsync();
      if (result.updated > 0) {
        toast.success({ title: t('notifications.markAllDone'), duration: 3500 });
      }
    } catch {
      toast.danger({ title: t('notifications.actionFailed'), duration: 5000 });
    }
  };

  const open = async (item: Notification | NotificationInboxItem): Promise<void> => {
    const row = 'source' in item ? item.source : item;
    if (row.readAt === null) {
      await markRead(row.id);
    }
    opts.onBeforeNavigate?.();
    openNotificationAction(row.actionUrl);
  };

  return {
    filter,
    setFilter,
    items,
    total,
    unreadCount,
    shown,
    page,
    pageCount,
    hasMore,
    isLoading: list.isPending,
    isFetching: list.isFetching,
    isBusy,
    error: list.error,
    refresh,
    loadMore,
    setPage,
    markRead,
    dismiss,
    markAllRead,
    open,
  };
}

function uniqueNotifications(rows: readonly Notification[]): Notification[] {
  const seen = new Set<string>();
  const out: Notification[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}
